'use client';
import React, { useState, useRef } from 'react';
import Link from 'next/link';
import PublicNav from '@/components/PublicNav';
import HomeFooter from '@/app/components/HomeFooter';
import {
  CheckCircle, Upload, FileText, Download, ArrowRight,
  User, Phone, Mail, MapPin, Briefcase, ChevronRight,
  Shield, Clock, Star, X, Loader2
} from 'lucide-react';

const verificationSteps = [
  { step: 1, title: 'Identity Verification', desc: 'Submit your South African ID for verification.' },
  { step: 2, title: '30-Min Work-Readiness Interview', desc: 'A structured conversation measuring 5 key dimensions.' },
  { step: 3, title: 'Reference Check', desc: 'We contact at least one professional or personal reference.' },
  { step: 4, title: 'Optional Police Clearance', desc: 'Add a police clearance for roles requiring higher trust.' },
  { step: 5, title: 'Receive Work-Readiness Profile', desc: 'Your verified profile goes live in the Handlenders directory.' },
];

const dimensions = [
  { name: 'Integrity', weight: '30%' },
  { name: 'Energy', weight: '25%' },
  { name: 'Learning Ability', weight: '20%' },
  { name: 'Humility', weight: '15%' },
  { name: 'Practical Readiness', weight: '10%' },
];

interface ApplyForm {
  fullName: string;
  email: string;
  phone: string;
  province: string;
  jobType: string;
  idNumber: string;
}

const provinces = [
  'Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape',
  'Limpopo', 'Mpumalanga', 'North West', 'Free State', 'Northern Cape',
];

const jobTypes = [
  'General Labour', 'Retail & Sales', 'Hospitality', 'Construction',
  'Domestic Work', 'Security', 'Driving', 'Administration', 'Other',
];

export default function CandidatePage() {
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [cvUploaded, setCvUploaded] = useState(false);
  const [cvReviewing, setCvReviewing] = useState(false);
  const [cvReviewed, setCvReviewed] = useState(false);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [applyStep, setApplyStep] = useState(1);
  const [applySubmitted, setApplySubmitted] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyForm, setApplyForm] = useState<ApplyForm>({
    fullName: '', email: '', phone: '', province: '', jobType: '', idNumber: '',
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cvFileInputRef = useRef<HTMLInputElement>(null);

  const handleCvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCvFile(file);
      setCvUploaded(true);
      setCvReviewing(true);
      setTimeout(() => {
        setCvReviewing(false);
        setCvReviewed(true);
      }, 2500);
    }
  };

  const handleDropZoneClick = () => {
    cvFileInputRef.current?.click();
  };

  const handleApplyFormChange = (field: keyof ApplyForm, value: string) => {
    setApplyForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyNext = () => {
    if (applyStep < 3) setApplyStep((s) => s + 1);
  };

  const handleApplyBack = () => {
    if (applyStep > 1) setApplyStep((s) => s - 1);
  };

  const handleApplySubmit = async () => {
    setApplyLoading(true);
    await new Promise((r) => setTimeout(r, 1800));
    setApplyLoading(false);
    setApplySubmitted(true);
  };

  const step1Valid = applyForm.fullName && applyForm.email && applyForm.phone;
  const step2Valid = applyForm.province && applyForm.jobType;
  const step3Valid = applyForm.idNumber.length >= 13;

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      {/* Hero */}
      <section className="relative overflow-hidden py-16 lg:py-24" style={{ background: 'var(--charcoal)' }}>
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-10 pointer-events-none" style={{ background: 'var(--amber)', transform: 'translate(30%, 30%)' }} />
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-widest mb-4 text-white/60">For Candidates</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight" style={{ fontFamily: 'var(--font-serif)' }}>
              Prove Your Work Readiness.<br />Get Noticed.
            </h1>
            <p className="text-lg text-white/75 mb-8 leading-relaxed">
              Become a Handlenders Verified Candidate. Complete our structured assessment and get visible to employers across South Africa.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowApplyForm(true)}
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-base transition-all duration-150 active:scale-95"
                style={{ background: 'var(--amber)', color: 'var(--charcoal)' }}
              >
                Apply to be Verified <ChevronRight size={18} />
              </button>
              <a
                href="#cv"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-base border-2 transition-all duration-150"
                style={{ borderColor: 'rgba(255,255,255,0.4)', color: 'white' }}
              >
                Improve My CV — R50
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <div className="border-b" style={{ background: 'var(--off-white)' }}>
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-4 flex flex-wrap gap-6">
          {[
            { icon: Shield, text: 'Free to apply' },
            { icon: Clock, text: 'Interview within 5 business days' },
            { icon: Star, text: 'Profile live in 7–10 days' },
            { icon: CheckCircle, text: 'POPIA compliant' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--charcoal)' }}>
              <item.icon size={15} style={{ color: 'var(--amber)' }} />
              {item.text}
            </div>
          ))}
        </div>
      </div>

      {/* Verification Process */}
      <section id="verify" className="py-16 lg:py-24 bg-white">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="mb-12">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--charcoal)' }}>The Process</p>
            <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
              Become a Handlenders Verified Candidate
            </h2>
            <p className="text-muted-foreground max-w-xl leading-relaxed">
              Free to apply. Complete our structured verification process and get your work-readiness profile published to the directory.
            </p>
          </div>

          {/* Steps grid */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-12">
            {verificationSteps.map((s, i) => (
              <div
                key={s.step}
                className="rounded-2xl p-5 relative"
                style={{
                  background: i === 0 ? 'var(--charcoal)' : i === 1 ? 'var(--amber)' : 'var(--off-white)',
                  color: i === 0 ? 'white' : i === 1 ? 'var(--charcoal)' : 'var(--charcoal)',
                }}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mb-3"
                  style={{
                    background: i === 0 ? 'rgba(255,255,255,0.15)' : i === 1 ? 'var(--charcoal)' : 'white',
                    color: i === 0 ? 'white' : i === 1 ? 'var(--amber)' : 'var(--charcoal)',
                  }}
                >
                  {s.step}
                </div>
                <div className="text-xs font-bold mb-1.5">{s.title}</div>
                <div className="text-xs leading-relaxed opacity-75">{s.desc}</div>
              </div>
            ))}
          </div>

          {/* Apply CTA */}
          <div className="rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6" style={{ background: 'var(--off-white)' }}>
            <div>
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
                Ready to get verified?
              </h3>
              <p className="text-sm text-muted-foreground">
                Fill in your details and we'll schedule your Work-Readiness Interview within 5 business days.
              </p>
            </div>
            <button
              onClick={() => setShowApplyForm(true)}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-sm transition-all duration-150 active:scale-95 flex-shrink-0"
              style={{ background: 'var(--charcoal)', color: 'var(--amber)' }}
            >
              Apply Now — It's Free <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* CV Improvement Service */}
      <section id="cv" className="py-16 lg:py-24" style={{ background: 'var(--off-white)' }}>
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--charcoal)' }}>Service — R50</p>
              <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
                AI-Powered CV Improvement
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Upload your CV and our AI reviews your formatting and content, then generates an improved professional version ready for download.
              </p>
              <div className="flex flex-col gap-3 mb-6">
                {[
                  { icon: Upload, label: 'Upload your CV (PDF or DOCX)' },
                  { icon: FileText, label: 'AI reviews formatting and content' },
                  { icon: CheckCircle, label: 'Preview your improved CV' },
                  { icon: Download, label: 'Pay R50 and download' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'white' }}>
                      <item.icon size={15} style={{ color: 'var(--charcoal)' }} />
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'var(--charcoal)' }}>{item.label}</span>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground">
                Payment via: <span className="font-semibold">PayFast · Peach Payments · Ozow</span>
              </div>
            </div>

            <div className="rounded-2xl p-8 border" style={{ background: 'white', borderColor: 'var(--border)' }}>
              <div className="text-center mb-6">
                <div className="text-4xl font-bold mb-1" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>R50</div>
                <div className="text-sm text-muted-foreground">One-time payment · Instant download</div>
              </div>

              <input
                ref={cvFileInputRef}
                type="file"
                accept=".pdf,.docx,.doc"
                className="hidden"
                onChange={handleCvFileChange}
              />

              {!cvUploaded && (
                <div>
                  <div
                    className="border-2 border-dashed rounded-xl p-8 text-center mb-4 cursor-pointer transition-colors hover:border-amber-400"
                    style={{ borderColor: 'var(--border)' }}
                    onClick={handleDropZoneClick}
                  >
                    <Upload size={32} className="mx-auto mb-3" style={{ color: 'var(--muted-foreground)' }} />
                    <p className="text-sm font-medium mb-1" style={{ color: 'var(--charcoal)' }}>Drop your CV here or click to browse</p>
                    <p className="text-xs text-muted-foreground">PDF or DOCX · Max 5MB</p>
                  </div>
                  <button onClick={handleDropZoneClick} className="w-full btn-primary py-3">
                    Upload CV
                  </button>
                </div>
              )}

              {cvUploaded && cvReviewing && (
                <div className="text-center py-8">
                  <Loader2 size={32} className="animate-spin mx-auto mb-4" style={{ color: 'var(--charcoal)' }} />
                  <p className="text-sm font-bold mb-1" style={{ color: 'var(--charcoal)' }}>AI is reviewing your CV...</p>
                  <p className="text-xs text-muted-foreground">Analysing formatting, content and structure</p>
                  {cvFile && (
                    <p className="text-xs mt-3 font-medium" style={{ color: 'var(--charcoal)' }}>{cvFile.name}</p>
                  )}
                </div>
              )}

              {cvUploaded && cvReviewed && (
                <div>
                  <div className="rounded-xl p-4 mb-4" style={{ background: 'var(--off-white)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle size={16} style={{ color: 'var(--charcoal)' }} />
                      <span className="text-sm font-bold" style={{ color: 'var(--charcoal)' }}>AI Review Complete</span>
                    </div>
                    <div className="space-y-2 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      <div className="flex items-center justify-between">
                        <span>Formatting</span>
                        <span className="font-semibold" style={{ color: 'var(--charcoal)' }}>Improved ✓</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Content clarity</span>
                        <span className="font-semibold" style={{ color: 'var(--charcoal)' }}>Enhanced ✓</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Professional tone</span>
                        <span className="font-semibold" style={{ color: 'var(--charcoal)' }}>Optimised ✓</span>
                      </div>
                    </div>
                  </div>
                  <button className="w-full btn-primary py-3 mb-2">
                    Pay R50 &amp; Download Improved CV
                  </button>
                  <button
                    onClick={() => { setCvUploaded(false); setCvReviewed(false); setCvFile(null); }}
                    className="w-full text-xs text-center py-2 hover:underline"
                    style={{ color: 'var(--muted-foreground)' }}
                  >
                    Upload a different CV
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* What We Assess */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-10 items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--charcoal)' }}>The Assessment</p>
              <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
                Five dimensions. One honest conversation.
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Our structured interview is not a test. It's a 30-minute conversation designed to understand who you are as a worker.
              </p>
              <Link href="/framework" className="text-sm font-bold" style={{ color: 'var(--charcoal)' }}>
                See the full framework →
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {dimensions.map((dim, i) => (
                <div
                  key={dim.name}
                  className="rounded-2xl p-5"
                  style={{
                    background: i === 0 ? 'var(--charcoal)' : 'var(--off-white)',
                    color: i === 0 ? 'white' : 'var(--charcoal)',
                    gridColumn: i === 0 ? 'span 2' : 'span 1',
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-sm">{dim.name}</span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: i === 0 ? 'var(--amber)' : 'white', color: 'var(--charcoal)' }}>
                      {dim.weight}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full" style={{ background: i === 0 ? 'rgba(255,255,255,0.15)' : 'var(--border)' }}>
                    <div className="h-1.5 rounded-full" style={{ background: i === 0 ? 'var(--amber)' : 'var(--charcoal)', width: dim.weight }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard CTA */}
      <section className="py-12 lg:py-16" style={{ background: 'var(--charcoal)' }}>
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-serif)' }}>Already applied or verified?</h3>
            <p className="text-white/60 text-sm">Access your candidate dashboard to track your verification status and view opportunities.</p>
          </div>
          <Link
            href="/worker-dashboard"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all duration-150 active:scale-95 flex-shrink-0"
            style={{ background: 'var(--amber)', color: 'var(--charcoal)' }}
          >
            Access Dashboard →
          </Link>
        </div>
      </section>

      <HomeFooter />

      {/* Apply to be Verified Modal */}
      {showApplyForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl" style={{ background: 'white' }}>
            {/* Modal header */}
            <div className="px-6 py-5 flex items-center justify-between" style={{ background: 'var(--charcoal)' }}>
              <div>
                <h3 className="font-bold text-white text-lg" style={{ fontFamily: 'var(--font-serif)' }}>Apply to be Verified</h3>
                {!applySubmitted && (
                  <p className="text-white/60 text-xs mt-0.5">Step {applyStep} of 3</p>
                )}
              </div>
              <button
                onClick={() => { setShowApplyForm(false); setApplyStep(1); setApplySubmitted(false); }}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-white/10"
              >
                <X size={16} color="white" />
              </button>
            </div>

            {/* Progress bar */}
            {!applySubmitted && (
              <div className="h-1" style={{ background: 'var(--border)' }}>
                <div
                  className="h-1 transition-all duration-300"
                  style={{ background: 'var(--amber)', width: `${(applyStep / 3) * 100}%` }}
                />
              </div>
            )}

            <div className="p-6">
              {applySubmitted ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--off-white)' }}>
                    <CheckCircle size={32} style={{ color: 'var(--charcoal)' }} />
                  </div>
                  <h4 className="text-xl font-bold mb-2" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
                    Application Submitted!
                  </h4>
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                    Thank you, <strong>{applyForm.fullName}</strong>. We've received your application and will contact you at <strong>{applyForm.email}</strong> within 2 business days to schedule your Work-Readiness Interview.
                  </p>
                  <div className="rounded-xl p-4 mb-6 text-left" style={{ background: 'var(--off-white)' }}>
                    <p className="text-xs font-bold mb-2" style={{ color: 'var(--charcoal)' }}>What happens next:</p>
                    <div className="space-y-1.5">
                      {['We verify your ID details', 'Interview scheduled within 5 business days', 'Reference check completed', 'Profile published to directory'].map((s, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold" style={{ background: 'var(--charcoal)', color: 'var(--amber)' }}>{i + 1}</div>
                          {s}
                        </div>
                      ))}
                    </div>
                  </div>
                  <Link
                    href="/sign-up-login"
                    className="btn-primary w-full py-3 justify-center"
                    onClick={() => setShowApplyForm(false)}
                  >
                    Create Your Account to Track Progress
                  </Link>
                </div>
              ) : (
                <>
                  {/* Step 1: Personal Details */}
                  {applyStep === 1 && (
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-bold mb-1" style={{ color: 'var(--charcoal)' }}>Personal Details</h4>
                        <p className="text-xs text-muted-foreground">Your information is kept private and POPIA compliant.</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--charcoal)' }}>Full Name *</label>
                        <div className="relative">
                          <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                          <input
                            type="text"
                            placeholder="Your full name"
                            value={applyForm.fullName}
                            onChange={(e) => handleApplyFormChange('fullName', e.target.value)}
                            className="input-field pl-9"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--charcoal)' }}>Email Address *</label>
                        <div className="relative">
                          <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                          <input
                            type="email"
                            placeholder="you@example.co.za"
                            value={applyForm.email}
                            onChange={(e) => handleApplyFormChange('email', e.target.value)}
                            className="input-field pl-9"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--charcoal)' }}>Mobile Number *</label>
                        <div className="relative">
                          <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                          <input
                            type="tel"
                            placeholder="+27 82 000 0000"
                            value={applyForm.phone}
                            onChange={(e) => handleApplyFormChange('phone', e.target.value)}
                            className="input-field pl-9"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Work Details */}
                  {applyStep === 2 && (
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-bold mb-1" style={{ color: 'var(--charcoal)' }}>Work Details</h4>
                        <p className="text-xs text-muted-foreground">Help us match you with the right opportunities.</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--charcoal)' }}>Province *</label>
                        <div className="relative">
                          <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                          <select
                            value={applyForm.province}
                            onChange={(e) => handleApplyFormChange('province', e.target.value)}
                            className="input-field pl-9 appearance-none"
                          >
                            <option value="">Select province</option>
                            {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--charcoal)' }}>Job Type *</label>
                        <div className="relative">
                          <Briefcase size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                          <select
                            value={applyForm.jobType}
                            onChange={(e) => handleApplyFormChange('jobType', e.target.value)}
                            className="input-field pl-9 appearance-none"
                          >
                            <option value="">Select job type</option>
                            {jobTypes.map((j) => <option key={j} value={j}>{j}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="rounded-xl p-4" style={{ background: 'var(--off-white)' }}>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          <strong style={{ color: 'var(--charcoal)' }}>Free to apply.</strong> The verification process is completely free. You only pay R50 if you choose the optional CV improvement service.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Step 3: ID Verification */}
                  {applyStep === 3 && (
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-bold mb-1" style={{ color: 'var(--charcoal)' }}>Identity Verification</h4>
                        <p className="text-xs text-muted-foreground">Your SA ID number is used only for verification and is never shared publicly.</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--charcoal)' }}>SA ID Number *</label>
                        <div className="relative">
                          <Shield size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                          <input
                            type="text"
                            placeholder="13-digit ID number"
                            maxLength={13}
                            value={applyForm.idNumber}
                            onChange={(e) => handleApplyFormChange('idNumber', e.target.value.replace(/\D/g, ''))}
                            className="input-field pl-9 tracking-widest"
                          />
                        </div>
                        <p className="text-xs mt-1 text-muted-foreground">{applyForm.idNumber.length}/13 digits</p>
                      </div>
                      <div className="rounded-xl p-4 border" style={{ borderColor: 'var(--border)', background: 'var(--off-white)' }}>
                        <p className="text-xs font-bold mb-1" style={{ color: 'var(--charcoal)' }}>Consent</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          By submitting this application, you consent to Handlenders verifying your identity, contacting your references, and publishing your anonymised work-readiness profile in the candidate directory. Your personal contact details are never shared publicly.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Navigation buttons */}
                  <div className="flex gap-3 mt-6">
                    {applyStep > 1 && (
                      <button
                        onClick={handleApplyBack}
                        className="flex-1 py-3 rounded-xl border font-semibold text-sm transition-all"
                        style={{ borderColor: 'var(--border)', color: 'var(--charcoal)' }}
                      >
                        Back
                      </button>
                    )}
                    {applyStep < 3 ? (
                      <button
                        onClick={handleApplyNext}
                        disabled={(applyStep === 1 && !step1Valid) || (applyStep === 2 && !step2Valid)}
                        className="flex-1 btn-primary py-3 disabled:opacity-40"
                      >
                        Continue <ArrowRight size={15} />
                      </button>
                    ) : (
                      <button
                        onClick={handleApplySubmit}
                        disabled={!step3Valid || applyLoading}
                        className="flex-1 btn-primary py-3 disabled:opacity-40"
                      >
                        {applyLoading ? (
                          <><Loader2 size={15} className="animate-spin" />Submitting...</>
                        ) : (
                          <>Submit Application <CheckCircle size={15} /></>
                        )}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
